/**
 * TenantBrand
 *
 * Three-layer fallback brand display:
 *   1. Tenant icon  (TENANT_ICON_MAP)  — compact mark
 *   2. Tenant logo  (TENANT_LOGO_MAP)  — full horizontal logo
 *   3. ButterLogo   variant="full"     — generic fallback
 *
 * Use `preferLogo` to skip the icon layer and go straight to the logo.
 * This is useful in contexts where a full-width lockup is always preferred
 * over a compact icon, even when an icon is registered.
 *
 * @example
 * // Default: icon → logo → butter
 * <TenantBrand tenantHandle="sweet-score" />
 *
 * // Skip icon layer: logo → butter
 * <TenantBrand tenantHandle="sweet-score" preferLogo />
 */

import { BrandAsset } from './BrandAsset';
import type { BrandSurface } from './resolveBrandAsset';
import type { LogoSizeTier } from './sizing';

interface TenantBrandProps {
  /**
   * Tenant to render. Defaults to the surrounding `TenantProvider`'s handle;
   * pass explicitly to override.
   */
  tenantHandle?: string;
  /**
   * Size tier (shape-aware, shared with TenantLogo). When set, the rendered mark
   * is sized by its resolved shape — a compact icon as a square, a full logo by
   * its registry `logoSize`. Ignored when `className` is set. Defaults to `h-8 w-auto`
   * when neither is provided.
   */
  size?: LogoSizeTier;
  /** Override sizing entirely. Wins over `size`. */
  className?: string;
  /**
   * When true, skips the icon layer and resolves: logo → ButterLogo.
   * Useful in contexts that always want a horizontal lockup.
   */
  preferLogo?: boolean;
  ariaHidden?: boolean;
  /**
   * The ground it renders on. `dark` prefers a registered dark-surface logo
   * over the regular one and flips the Butter fallback's ink.
   */
  surface?: BrandSurface;
  /**
   * Crop override, normally the registry's `logoViewBox`. Mainly for the
   * Playground.
   *
   * **Ignored when the icon layer wins** — a logo crop must not be applied to an
   * icon, which is separate artwork with its own viewBox. `BrandAsset` enforces
   * that; passing it here for an icon-resolving tenant is a no-op, not a bug.
   */
  viewBox?: string;
  /** Plate-rounding override, normally the registry's `roundedLogo`. Mainly for the Playground. */
  rounded?: boolean;
  /** Size multiplier, normally the registry's `dark.scale`. Mainly for the Playground. */
  scale?: number;
}

export function TenantBrand({
  tenantHandle,
  size,
  className,
  preferLogo = false,
  ariaHidden = true,
  surface = 'light',
  viewBox,
  rounded,
  scale,
}: TenantBrandProps) {
  return (
    <BrandAsset
      // `brand` is icon → logo → Butter; `preferLogo` drops to the plain logo chain.
      kind={preferLogo ? 'logo' : 'brand'}
      tenantHandle={tenantHandle}
      size={size}
      // Legacy default when the caller gives neither size nor className.
      defaultClassName="h-8 w-auto"
      className={className}
      ariaHidden={ariaHidden}
      surface={surface}
      viewBox={viewBox}
      rounded={rounded}
      scale={scale}
    />
  );
}
